package com.taocent.simple.compose.component.richtext.core.internal.undo

import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.input.TextFieldValue

internal sealed class UndoOperation {
    data class TextChange(
        val offset: Int,
        val removed: String,
        val inserted: String
    ) : UndoOperation()

    data class SpanChange(
        val previousSpans: List<AnnotatedString.Range<SpanStyle>>,
        val newSpans: List<AnnotatedString.Range<SpanStyle>>
    ) : UndoOperation()

    data class Composite(
        val operations: List<UndoOperation>
    ) : UndoOperation()
}

internal class UndoManager(
    private val maxSize: Int = 100,
    private val mergeIntervalMs: Long = 500
) {

    private class UndoEntry(
        val diff: UndoOperation,
        val previousSpans: List<AnnotatedString.Range<SpanStyle>>,
        val newSpans: List<AnnotatedString.Range<SpanStyle>>,
        val previousAnnotations: List<AnnotatedString.Range<String>> = emptyList(),
        val newAnnotations: List<AnnotatedString.Range<String>> = emptyList()
    )

    /**
     * 栈/时间/容量/canUndo/canRedo 等不变式委托给 [UndoStackCore]。
     * 本类只关心:diff 计算、合并策略(tryMergeDiffs)、undo/redo 时 spans/annotations 的回填。
     */
    private val core = UndoStackCore<UndoEntry>(maxSize, mergeIntervalMs)

    val canUndo: Boolean get() = core.canUndo
    val canRedo: Boolean get() = core.canRedo

    fun record(value: TextFieldValue) {
        val diff = computeTextDiff("", value.annotatedString.text)

        if (diff is UndoOperation.TextChange && diff.removed.isEmpty() && diff.inserted.isEmpty()) return

        val entry = UndoEntry(diff = diff, previousSpans = emptyList(), newSpans = emptyList())

        if (core.isInMergeWindow()) {
            val top = core.peekUndo() ?: return
            val merged = tryMergeDiffs(top.diff, entry.diff)
            if (merged != null) {
                core.replaceUndoTop(
                    UndoEntry(merged, top.previousSpans, entry.newSpans, top.previousAnnotations, entry.newAnnotations)
                )
                return
            }
        }

        core.pushUndo(entry)
    }

    fun record(currentValue: TextFieldValue, newValue: TextFieldValue) {
        val oldText = currentValue.annotatedString.text
        val newText = newValue.annotatedString.text
        val oldSpans = currentValue.annotatedString.spanStyles
        val newSpans = newValue.annotatedString.spanStyles
        val oldAnns = currentValue.annotatedString.getStringAnnotations(0, oldText.length).toList()
        val newAnns = newValue.annotatedString.getStringAnnotations(0, newText.length).toList()

        val textDiff = computeTextDiff(oldText, newText)
        val hasTextChange = textDiff !is UndoOperation.TextChange ||
            textDiff.removed.isNotEmpty() || textDiff.inserted.isNotEmpty()
        val hasSpanChange = oldSpans != newSpans
        val hasAnnotationChange = oldAnns != newAnns

        if (!hasTextChange && !hasSpanChange && !hasAnnotationChange) return

        val mergedEntry: UndoEntry? = if (hasTextChange && core.isInMergeWindow()) {
            val top = core.peekUndo()
            val topTextDiff = when (val d = top?.diff) {
                is UndoOperation.TextChange -> d
                is UndoOperation.Composite -> d.operations.filterIsInstance<UndoOperation.TextChange>().firstOrNull()
                else -> null
            }
            if (top != null && topTextDiff != null) {
                val merged = tryMergeDiffs(topTextDiff, textDiff)
                if (merged != null) {
                    val newDiff = if (hasSpanChange || hasAnnotationChange) {
                        UndoOperation.Composite(listOf(merged, UndoOperation.SpanChange(oldSpans, newSpans)))
                    } else {
                        merged
                    }
                    UndoEntry(newDiff, top.previousSpans, newSpans, top.previousAnnotations, newAnns)
                } else null
            } else null
        } else null

        if (mergedEntry != null) {
            core.replaceUndoTop(mergedEntry)
            return
        }

        val diff = when {
            hasTextChange && (hasSpanChange || hasAnnotationChange) -> UndoOperation.Composite(
                listOf(textDiff, UndoOperation.SpanChange(oldSpans, newSpans))
            )
            hasSpanChange -> UndoOperation.SpanChange(oldSpans, newSpans)
            else -> textDiff
        }
        core.pushUndo(UndoEntry(diff, oldSpans, newSpans, oldAnns, newAnns))
    }

    fun undo(currentValue: TextFieldValue): TextFieldValue? {
        val entry = core.popLastUndo() ?: return null

        val curAnns = currentValue.annotatedString.getStringAnnotations(0, currentValue.annotatedString.text.length).toList()
        val redoEntry = UndoEntry(
            diff = entry.diff,
            previousSpans = currentValue.annotatedString.spanStyles,
            newSpans = entry.newSpans,
            previousAnnotations = curAnns,
            newAnnotations = entry.newAnnotations
        )
        core.pushRedo(redoEntry)

        return applyUndoRedo(currentValue, entry, isUndo = true)
    }

    fun redo(currentValue: TextFieldValue): TextFieldValue? {
        val entry = core.popLastRedo() ?: return null

        val curAnns = currentValue.annotatedString.getStringAnnotations(0, currentValue.annotatedString.text.length).toList()
        val undoEntry = UndoEntry(
            diff = entry.diff,
            previousSpans = currentValue.annotatedString.spanStyles,
            newSpans = entry.newSpans,
            previousAnnotations = curAnns,
            newAnnotations = entry.newAnnotations
        )
        // redo 路径:不重置 lastRecordMark,也不清空 redo 栈,只把 entry 推回 undo 栈
        core.pushUndo(undoEntry, clearRedo = false)

        return applyUndoRedo(currentValue, entry, isUndo = false)
    }

    fun clear() {
        core.clear()
    }

    private fun applyUndoRedo(
        currentValue: TextFieldValue,
        entry: UndoEntry,
        isUndo: Boolean
    ): TextFieldValue {
        val op = entry.diff
        val currentText = currentValue.annotatedString.text

        val newText: String
        val resultSpans: List<AnnotatedString.Range<SpanStyle>>
        val resultAnnotations: List<AnnotatedString.Range<String>>

        when (op) {
            is UndoOperation.TextChange -> {
                val appliedOp = if (isUndo) reverseDiff(op) else op
                newText = applyDiff(currentText, appliedOp)
                resultSpans = if (isUndo) entry.previousSpans else entry.newSpans
                resultAnnotations = if (isUndo) entry.previousAnnotations else entry.newAnnotations
            }
            is UndoOperation.SpanChange -> {
                newText = currentText
                resultSpans = if (isUndo) op.previousSpans else op.newSpans
                resultAnnotations = if (isUndo) entry.previousAnnotations else entry.newAnnotations
            }
            is UndoOperation.Composite -> {
                var text = currentText
                val ops = if (isUndo) op.operations.reversed() else op.operations
                for (subOp in ops) {
                    when (subOp) {
                        is UndoOperation.TextChange -> {
                            val appliedOp = if (isUndo) reverseDiff(subOp) else subOp
                            text = applyDiff(text, appliedOp)
                        }
                        else -> {}
                    }
                }
                newText = text
                resultSpans = if (isUndo) entry.previousSpans else entry.newSpans
                resultAnnotations = if (isUndo) entry.previousAnnotations else entry.newAnnotations
            }
        }

        val restoredAnnotated = AnnotatedString.Builder(newText.length).apply {
            pushStyle(SpanStyle())
            append(newText)
            pop()
            resultSpans.forEach { addStyle(it.item, it.start, it.end) }
            resultAnnotations.forEach { ann ->
                val safeStart = ann.start.coerceIn(0, newText.length)
                val safeEnd = ann.end.coerceIn(0, newText.length)
                if (safeStart < safeEnd) {
                    addStringAnnotation(ann.tag, ann.item, safeStart, safeEnd)
                }
            }
        }.toAnnotatedString()

        val cursorPos = computeCursorAfterUndoRedo(op, isUndo, newText.length)

        return TextFieldValue(
            annotatedString = restoredAnnotated,
            selection = TextRange(cursorPos.coerceIn(0, newText.length))
        )
    }

    private fun computeCursorAfterUndoRedo(op: UndoOperation, isUndo: Boolean, textLength: Int): Int {
        return when (op) {
            is UndoOperation.TextChange -> {
                if (isUndo) op.offset else (op.offset + op.inserted.length).coerceAtMost(textLength)
            }
            is UndoOperation.SpanChange -> textLength
            is UndoOperation.Composite -> {
                val textOp = op.operations.filterIsInstance<UndoOperation.TextChange>().firstOrNull()
                if (textOp != null) {
                    if (isUndo) textOp.offset else (textOp.offset + textOp.inserted.length).coerceAtMost(textLength)
                } else {
                    textLength
                }
            }
        }
    }
}

private fun tryMergeDiffs(first: UndoOperation, second: UndoOperation): UndoOperation? {
    if (first is UndoOperation.TextChange && second is UndoOperation.TextChange) {
        val endOfFirst = first.offset + first.inserted.length
        if (second.offset == first.offset || second.offset == endOfFirst) {
            val combinedRemoved = first.removed + second.removed
            val combinedInserted = if (second.offset == first.offset) {
                second.inserted + first.inserted
            } else {
                first.inserted + second.inserted
            }
            return UndoOperation.TextChange(first.offset, combinedRemoved, combinedInserted)
        }
    }
    return null
}

internal fun computeTextDiff(oldText: String, newText: String): UndoOperation {
    if (oldText == newText) {
        return UndoOperation.TextChange(0, "", "")
    }

    val minLen = minOf(oldText.length, newText.length)
    var commonPrefix = 0
    while (commonPrefix < minLen && oldText[commonPrefix] == newText[commonPrefix]) {
        commonPrefix++
    }

    var oldSuffix = oldText.length
    var newSuffix = newText.length
    while (oldSuffix > commonPrefix && newSuffix > commonPrefix &&
        oldText[oldSuffix - 1] == newText[newSuffix - 1]
    ) {
        oldSuffix--
        newSuffix--
    }

    val removed = oldText.substring(commonPrefix, oldSuffix)
    val inserted = newText.substring(commonPrefix, newSuffix)

    return UndoOperation.TextChange(commonPrefix, removed, inserted)
}

internal fun reverseDiff(op: UndoOperation): UndoOperation {
    return when (op) {
        is UndoOperation.TextChange -> UndoOperation.TextChange(op.offset, op.inserted, op.removed)
        is UndoOperation.SpanChange -> UndoOperation.SpanChange(op.newSpans, op.previousSpans)
        is UndoOperation.Composite -> UndoOperation.Composite(
            op.operations.reversed().map { reverseDiff(it) }
        )
    }
}

internal fun applyDiff(text: String, op: UndoOperation): String {
    return when (op) {
        is UndoOperation.TextChange -> {
            val before = text.substring(0, op.offset.coerceAtMost(text.length))
            val after = text.substring((op.offset + op.removed.length).coerceAtMost(text.length))
            before + op.inserted + after
        }
        is UndoOperation.SpanChange -> text
        is UndoOperation.Composite -> {
            op.operations.fold(text) { acc, subOp -> applyDiff(acc, subOp) }
        }
    }
}
